import React from 'react';
import { BadgeProps } from '@/shared/types/badge';
import { mergeGroupedBadgeProps, omitBadgeConsumedProps } from './utils/mergeGroupedBadgeProps';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { Icon } from '..';
import { IconProps } from '@/index';
import { TruncatedWithTooltip } from '@/modules/Tooltip';

/**
 * Badge - Componente de insignia/badge versátil y configurable
 *
 * Componente que muestra una insignia con soporte para diferentes tamaños, colores, temas,
 * iconos, estados de carga y personalización avanzada.
 *
 * Con `hover={false}` el chip no captura el puntero (útil si el padre con `group` controla el remarcado).
 *
 * @example
 * ```tsx
 * // API agrupada (preferida)
 * <Badge appearance={{ color: 'danger', size: 'sm' }} text={{ label: '3' }} />
 *
 * // Dot sin label
 * <Badge appearance={{ size: 'dot', color: 'accent' }} />
 *
 * // Con icono
 * <Badge appearance={{ color: 'success', size: 'md' }} text={{ label: 'Nuevo' }} glyph={{ icon: 'StarOutlined' }} />
 *
 * // Carga
 * <Badge appearance={{ color: 'accent' }} state={{ loading: true }} />
 * ```
 *
 * @see {@link BadgeProps} para la lista completa de props
 */
export const Badge = (props: BadgeProps) => {
  const {
    label = '',
    color = 'accent',
    theme = 'solid',
    size = 'dot',
    stroke = false,
    hover = true,
    disabled = false,
    className,
    icon,
    iconSlot,
    iconDuotonePrimary,
    iconDuotoneSecondary,
    iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary,
    customColor = '',
    loading = false,
    truncateLabel = false,
    truncateTooltip = false,
  } = mergeGroupedBadgeProps(props);
  const htmlRest = omitBadgeConsumedProps(props);
  // Normalizar label: convertir a string si existe, o string vacío si es null/undefined
  const normalizedLabel = label !== null ? String(label) : '';

  // Determinar si tiene contenido válido para mostrar
  const hasLabel = normalizedLabel !== ''; // Si label es "0", normalizedLabel será "0" (string), así que hasLabel será true
  const hasIcon = (!!icon || (iconSlot !== undefined && iconSlot !== null)) && size !== 'dot';

  // Si está en modo loading, siempre mostrar (incluso sin label/icon)
  // Si es dot, siempre se muestra (no necesita label ni icon)
  // Si no es dot y no tiene label ni icon ni loading, no mostrar
  if (size !== 'dot' && !hasLabel && !hasIcon && !loading) {
    return null;
  }

  const labelLen = normalizedLabel.length;
  const isDot = size === 'dot';
  const isSm = size === 'sm';
  const isSmOneChar = isSm && labelLen === 1;
  const isSmMulti = isSm && labelLen > 1;

  // =========================
  // Variants base
  // =========================
  const badgeVariants = cva(
    'ui-inline-flex ui-items-center ui-justify-center ui-font-semibold ui-leading-none ui-overflow-hidden ui-text-body-xs-regular ui-tracking-body-xs',
    {
      variants: {
        color: {
          accent: '',
          gray: '',
          success: '',
          warning: '',
          danger: '',
          info: '',
        },
        theme: {
          solid: '',
          soft: '',
        },
        size: {
          dot: 'ui-w-12 ui-h-12 ui-rounded-control-pill',
          sm: 'ui-rounded',
          md: 'ui-rounded-control-sm ui-px-8 ui-py-2',
          lg: 'ui-rounded-control-sm ui-px-8 ui-py-4',
        },
      },
      defaultVariants: {
        color: 'accent',
        theme: 'solid',
        size: 'dot',
      },
    },
  );

  // =========================
  // Mapas de color por theme
  // =========================
  const mapSolid: Record<string, string> = {
    accent: 'ui-bg-fill-brand ui-text-on-color ui-fill-on-color',
    gray: 'ui-bg-surface-tertiary ui-text-primary ui-fill',
    success: 'ui-bg-fill-success ui-text-on-color ui-fill-on-color',
    warning: 'ui-bg-fill-warning ui-text-on-color ui-fill-on-color',
    danger: 'ui-bg-fill-danger ui-text-on-color ui-fill-on-color',
    info: 'ui-bg-fill-info ui-text-on-color ui-fill-on-color',
  };

  const mapSoft: Record<string, string> = {
    accent: 'ui-bg-surface-brand-hover ui-text-brand ui-fill-brand',
    gray: 'ui-bg-fill-tertiary ui-text-primary ui-fill-secondary',
    success: 'ui-bg-surface-success-hover ui-text-success ui-fill-success',
    warning: 'ui-bg-surface-warning-hover ui-text-warning ui-fill-warning',
    danger: 'ui-bg-surface-danger ui-text-danger ui-fill-brand',
    info: 'ui-bg-surface-info-hover ui-text-info ui-fill-danger-hover',
  };

  // =========================
  // Disabled overrides
  // =========================
  const disabledSolid = 'ui-bg-fill-disabled ui-text-disabled ui-fill-disabled';
  const disabledSoft = 'ui-bg-surface-disabled ui-text-disabled ui-fill-disabled';
  const disabledStroke = 'ui-border-disabled';

  const themeClasses = disabled
    ? theme === 'soft'
      ? disabledSoft
      : disabledSolid
    : theme === 'soft'
      ? mapSoft[color]
      : mapSolid[color];

  // =========================
  // Stroke
  // =========================
  const strokeSolid: Record<string, string> = {
    accent: 'ui-border-brand',
    gray: 'ui-border-default',
    success: 'ui-border-success',
    warning: 'ui-border-warning',
    danger: 'ui-border-danger',
    info: 'ui-border-info',
  };

  const strokeSoft: Record<string, string> = {
    accent: 'ui-border-brand',
    gray: 'ui-border-default',
    success: 'ui-border-success',
    warning: 'ui-border-warning',
    danger: 'ui-border-danger',
    info: 'ui-border-info',
  };

  const strokeClasses = disabled
    ? stroke
      ? disabledStroke
      : ''
    : stroke
      ? theme === 'soft'
        ? strokeSoft[color]
        : strokeSolid[color]
      : '';

  // =========================
  // Modificadores por tamaño (hooks)
  // =========================
  const smOneCharClasses = isSmOneChar
    ? [
      'ui-justify-center ui-leading-none ui-rounded-control-pill',
      'ui-h-16 ui-w-16',
      'ui-p-4',
      hasIcon ? 'ui-gap-4 ui-px-8 ui-w-auto' : '',
    ].join(' ')
    : '';

  const smMultiClasses = isSmMulti
    ? [
      'ui-rounded-control-sm ui-h-16',
      'ui-px-8 ui-py-2',
      hasIcon ? 'ui-gap-4 ui-pl-8 ui-pr-8' : '',
    ].join(' ')
    : '';

  const dotClasses = isDot ? 'ui-rounded-control-pill' : '';

  // =========================
  // Icon sizing / gap
  // =========================
  // Mapea el size del Badge al size de tu Icon component
  const iconSizeByBadge: Record<'sm' | 'md' | 'lg' | 'dot', IconProps['size']> = {
    sm: 'xs',
    md: 'xs',
    lg: 'sm',
    dot: 'xs',
  };

  const iconGapClasses = hasIcon
    ? size === 'lg'
      ? 'ui-gap-4'
      : size === 'md'
        ? 'ui-gap-4'
        : size === 'sm'
          ? 'ui-gap-4'
          : ''
    : '';

  // Si icon es string => usamos tu <Icon name="..." />
  // Si es ReactElement => lo clonamos para inyectar clases/props
  const renderIcon = () => {
    if (!hasIcon) return null;
    if (iconSlot !== undefined && iconSlot !== null) {
      return (
        <span className="ui-inline-flex ui-shrink-0 ui-items-center ui-justify-center" aria-hidden>
          {iconSlot}
        </span>
      );
    }
    if (icon === undefined || icon === null) {
      return null;
    }
    return (
      <Icon
        name={icon}
        size={iconSizeByBadge[size]}
        //className="ui-shrink-0"
        // color/fill heredan del badge (ui-text-*, ui-fill-*)
        aria-hidden
        colorDuotonePrimary={iconDuotonePrimary}
        colorDuotoneSecondary={iconDuotoneSecondary}
        opacityDuotonePrimary={iconDuotoneOpacityPrimary}
        opacityDuotoneSecondary={iconDuotoneOpacitySecondary}
      />
    );
  };

  // Clases para tamaño fijo cuando está en loading
  const loadingSizeClasses = {
    dot: '', // dot ya tiene tamaño fijo ui-w-12 ui-h-12
    sm: 'ui-h-16 ui-w-16 ui-rounded-control-pill ui-justify-center', // sm necesita tamaño fijo y circular cuando está en loading (sin label)
    md: 'ui-h-16 ui-w-16 ui-rounded-control-pill ui-justify-center', // md necesita tamaño fijo y circular cuando está en loading
    lg: 'ui-h-20 ui-w-20 ui-rounded-control-pill ui-justify-center', // lg necesita tamaño fijo y circular cuando está en loading
  };

  // Spinner de carga que se adapta al tamaño del badge
  const renderSpinner = () => {
    if (!loading) return null;

    // Tamaños del spinner según el tamaño del badge
    // El spinner debe ser proporcional al badge (aproximadamente 60-65% del tamaño)
    // dot: 48px x 48px → spinner ~32px (ui-w-8 = 32px, ~67%)
    // sm: 64px x 64px → spinner ~40px (ui-w-10 = 40px, ~63%)
    // md: con min-h-16 min-w-16 = 64px → spinner ~40px (ui-w-10 = 40px, ~63%)
    // lg: con min-h-20 min-w-20 = 80px → spinner ~48px (ui-w-12 = 48px, ~60%)
    const spinnerSizeClasses = {
      dot: 'ui-w-8 ui-h-8 ui-border-default', // Badge dot es 48px (ui-w-12), spinner 32px (~67% del tamaño)
      sm: 'ui-w-10 ui-h-10 ui-border-default', // Badge sm es 64px (ui-h-16/ui-w-16), spinner 40px (~63% del tamaño)
      md: 'ui-w-10 ui-h-10 ui-border-default', // Badge md con min-h-16 min-w-16 = 64px, spinner 40px (~63% del tamaño)
      lg: 'ui-w-12 ui-h-12 ui-border-default', // Badge lg con min-h-20 min-w-20 = 80px, spinner 48px (~60% del tamaño)
    };

    // Asegurar que size tenga un valor válido (por defecto 'sm' si no está definido)
    const effectiveSize = size || 'sm';
    const spinnerClasses = spinnerSizeClasses[effectiveSize] || spinnerSizeClasses.sm;

    return (
      <span
        className={cn(
          'ui-inline-flex ui-max-h-full ui-max-w-full ui-shrink-0 ui-animate-spin ui-items-center ui-justify-center ui-rounded-full ui-border-solid ui-border-current ui-border-t-transparent',
          spinnerClasses,
        )}
        aria-label="Cargando"
        role="status"
      />
    );
  };

  // Estilos inline para customColor que sobrescriben los colores por defecto
  const customStyle = customColor
    ? {
      backgroundColor: customColor,
      color: theme === 'solid' ? '#ffffff' : customColor,
      borderColor: stroke ? customColor : undefined,
    }
    : undefined;

  return (
    <span
      className={cn(
        'ui-imj',
        badgeVariants({ color, size, theme }),
        themeClasses,
        stroke ? 'ui-border-1' : '',
        strokeClasses,
        dotClasses,
        smOneCharClasses,
        smMultiClasses,
        iconGapClasses,
        loading
          ? loadingSizeClasses[size as keyof typeof loadingSizeClasses] || loadingSizeClasses.sm
          : '', // Aplicar tamaño mínimo cuando está en loading
        !hover ? 'ui-pointer-events-none' : '',
        className,
      )}
      style={customStyle}
      {...htmlRest}
    >
      {/* Si está en modo loading, mostrar spinner */}
      {loading ? (
        renderSpinner()
      ) : (
        <>
          {/* size=dot: NO icon, NO label */}
          {!isDot && renderIcon()}
          {!isDot &&
            (truncateLabel ? (
              <TruncatedWithTooltip
                truncate
                title={normalizedLabel}
                showTooltipWhenTruncated={truncateTooltip}
                className="ui-min-w-0"
              >
                {normalizedLabel}
              </TruncatedWithTooltip>
            ) : (
              normalizedLabel
            ))}
        </>
      )}
    </span>
  );
};
